import { useCallback, useEffect, useMemo, useRef } from 'react';

import { Auth0Config } from '@/src/integrations/auth0';

import { type ApplicationContext, type LogoutOptions } from '../types';

export const useApplicationContext = () => {
  const navigateToLogout = useCallback(() => {
    window.location.assign(Auth0Config.fromEnvironment().logoutPath);
  }, []);

  const logoutChannel = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      return;
    }
    const channel = new BroadcastChannel('auth-logout');
    logoutChannel.current = channel;
    channel.onmessage = navigateToLogout;
    return () => {
      channel.onmessage = null;
      channel.close();
      logoutChannel.current = null;
    };
  }, [navigateToLogout]);

  const logout = useCallback(
    (opts?: LogoutOptions) => {
      if (logoutChannel.current) {
        logoutChannel.current.postMessage('logout');
      }
      if (opts?.navigateCurrentTab === false) {
        return;
      }
      navigateToLogout();
    },
    [navigateToLogout],
  );

  return useMemo<ApplicationContext>(() => ({ logout }), [logout]);
};
